import React from 'react';
import RestCrudThunks from './restCrudThunksGenerator';
import { ROLE_TYPES } from '../utils/DataConstants';
import { SITE_SURVEYS_GROUPED_CRUD_ACTION_CREATORS } from '../actions/site_surveys_grouped_actions';
import FetchUtils, { parseJSON } from './fetch_utils';
import { SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS } from '../actions/site_surveys_locationandwalkthrough_actions';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { safeEncodeURIComponent } from '../utils/HistoryUtils';
import { logoutSuccess } from '../actions/session_actions';
import { message } from 'antd';
import { TextButton } from '../components/text_button/TextButton';

const fetchUtils = new FetchUtils();

export const siteSurveysGroupedRestCrudThunksForBuyer = new RestCrudThunks(
	'site_surveys/grouped/title',
	'site_survey',
	'siteSurvey',
	'siteSurveys',
	SITE_SURVEYS_GROUPED_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'site_survey/'
);

export const siteSurveysGroupedRestCrudThunksForSupplier = new RestCrudThunks(
	'site_surveys/grouped/title',
	'site_survey',
	'siteSurvey',
	'siteSurveys',
	SITE_SURVEYS_GROUPED_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'site_survey/'
);

export const getSSLocationsByTitleAndTemplate = (roleType) => (title, templateId) => {
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			const encodedTitle = safeEncodeURIComponent(title);
			const encodedTemplateId = safeEncodeURIComponent(templateId);
			dispatch(SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.fetchStart());
			fetchUtils
				.get(
					getBackendUri() +
						`/api/v1/${roleType}/site_survey/site_surveys/grouped/title/${encodedTitle}/template/${encodedTemplateId}`,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(
							SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.fetchError(d.message, null)
						);
						reject(d.message);
					} else {
						dispatch(
							SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.fetchSuccess(
								d.data,
								d.count || d.data.length,
								null,
								null,
								null,
								null
							)
						);
						resolve(d.data);
					}
				})
				.catch((d) => {
					if (d.isCancelled) {
						console.log('Cancelled Promise....getSSLocationsByTitleAndTemplate');
					} else {
						dispatch(
							SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.fetchError(d.message, null)
						);
						reject(d.message);
					}
				});
		});
	};
};

const archiveOrUnarchiveGroupedSiteSurveys =
	(action: 'archive' | 'unarchive') =>
	(roleType) =>
	(title, templateId, showUndo = true) => {
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				const successLabel = action === 'archive' ? 'archived' : 'unarchived';
				const failureLabel = action;
				const encodedTitle = safeEncodeURIComponent(title);
				const encodedTemplateId = safeEncodeURIComponent(templateId);

				const sendRequest = () =>
					fetchUtils
						.post(
							getBackendUri() +
								`/api/v1/${roleType}/site_survey/site_surveys/grouped/title/${encodedTitle}/template/${encodedTemplateId}/${action}`,
							{
								headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
							}
						)
						.then(parseJSON)
						.then(({ json, status }) => {
							const d = json;
							if (status === 401) {
								dispatch(logoutSuccess());
							}
							if (d.status === 'error') {
								message.error(`Failed to ${failureLabel} site surveys.`);
								reject(d.message);
							} else {
								resolve(d.data);
							}
						})
						.catch((d) => {
							message.error(`Failed to ${failureLabel} site surveys.`);
							reject(d.message);
						});

				if (showUndo) {
					const requestTimeout = setTimeout(sendRequest, 4000);
					const cancelRequest = () => clearTimeout(requestTimeout);

					const hideUndo = message.success(
						React.createElement(
							'span',
							null,
							`Site surveys ${successLabel}. `,
							React.createElement(
								TextButton,
								{
									onClick: () => {
										cancelRequest();
										hideUndo();
										resolve(null);
									},
								},
								'Undo'
							)
						)
					);
				} else {
					sendRequest();
				}
			});
		};
	};

export const archiveGroupedSiteSurveys = archiveOrUnarchiveGroupedSiteSurveys('archive');
export const unarchiveGroupedSiteSurveys = archiveOrUnarchiveGroupedSiteSurveys('unarchive');

const archiveOrUnarchiveGroupedSiteSurveysByLocation =
	(action: 'archive' | 'unarchive') =>
	(roleType) =>
	(title, templateId, locationId, showUndo = false) => {
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				const successLabel = action === 'archive' ? 'archived' : 'unarchived';
				const failureLabel = action;
				const encodedTitle = safeEncodeURIComponent(title);
				const encodedTemplateId = safeEncodeURIComponent(templateId);
				const encodedLocationId = encodeURIComponent(locationId);

				const sendRequest = () =>
					fetchUtils
						.post(
							getBackendUri() +
								`/api/v1/${roleType}/site_survey/site_surveys/grouped/title/${encodedTitle}/template/${encodedTemplateId}/location/${encodedLocationId}/${action}`,
							{
								headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
							}
						)
						.then(parseJSON)
						.then(({ json, status }) => {
							const d = json;
							if (status === 401) {
								dispatch(logoutSuccess());
							}
							if (d.status === 'error') {
								message.error(`Failed to ${failureLabel} site surveys.`);
								reject(d.message);
							} else {
								message.success(`Site surveys ${successLabel}.`);
								resolve(d.data);
							}
						})
						.catch((d) => {
							message.error(`Failed to ${failureLabel} site surveys.`);
							reject(d.message);
						});

				if (showUndo) {
					const requestTimeout = setTimeout(sendRequest, 4000);
					const cancelRequest = () => clearTimeout(requestTimeout);

					const hideUndo = message.success(
						React.createElement(
							'span',
							null,
							`Site surveys ${successLabel}. `,
							React.createElement(
								TextButton,
								{
									onClick: () => {
										cancelRequest();
										hideUndo();
										resolve(null);
									},
								},
								'Undo'
							)
						)
					);
				} else {
					sendRequest();
				}
			});
		};
	};

export const archiveGroupedSiteSurveysByLocation =
	archiveOrUnarchiveGroupedSiteSurveysByLocation('archive');
export const unarchiveGroupedSiteSurveysByLocation =
	archiveOrUnarchiveGroupedSiteSurveysByLocation('unarchive');

export const siteSurveysLocationAndWalkthroughDetail = (roleType) => ({
	read: (
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) => {
		if (pagination) {
			const paginationQuery = {
				offset: (pagination.current - 1) * pagination.pageSize,
				limit: pagination.pageSize,
			};
			params = {
				...params,
				...paginationQuery,
			};
		}

		// if sorting present, fix order naming and add to query params
		if (sorting) {
			const sortingQuery = { ...sorting, order: sorting.order === 'ascend' ? 'asc' : 'desc' };
			params = {
				...params,
				...sortingQuery,
			};
		}
		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(
					SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.fetchStart(targetCollectionName)
				);

				fetchUtils
					.get(
						getBackendUri() +
							`/api/v1/${roleType}/site_survey/site_surveys/grouped/title/${safeEncodeURIComponent(
								filters.title
							)}/template/${safeEncodeURIComponent(filters.templateId)}`,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							dispatch(
								SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.fetchError(
									d.message,
									targetCollectionName
								)
							);
							reject(d.message);
						} else {
							dispatch(
								SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.fetchSuccess(
									d.data,
									d.count,
									targetCollectionName,
									{
										...pagination,
										total: d.count,
									},
									sorting,
									filters,
									addToTargetCollection
								)
							);
							resolve(d.data);
						}
					})
					.catch((d) => {
						if (d.isCancelled) {
							console.log(`Cancelled Promise....${targetCollectionName}`);
						} else {
							dispatch(
								SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.fetchError(
									d.message,
									targetCollectionName
								)
							);
							reject(d.message);
						}
					});
			});
		};
	},
});
